"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectItem } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, X, Upload, Image as ImageIcon, CheckCircle, Loader2 } from "lucide-react";
import Image from "next/image";
import { clientLogger } from "@/lib/client-logger";

interface Schema {
  id: string;
  schemaName: string;
  campaignId: string | null;
}

interface UploadedMedia {
  cid: string;
  url: string;
  filename: string;
  isVideo: boolean;
}

type TemplateStatus = "PENDING" | "CREATING" | "CREATED" | "FAILED";

interface CreatedTemplate {
  id: string;
  onChainTemplateId: string | null;
  name: string;
  status: TemplateStatus;
  imageUrl: string | null;
  videoUrl: string | null;
}

interface TemplateCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (template: CreatedTemplate) => void;
  schemaId?: string;
  campaignId?: string;
}

export function TemplateCreationModal({
  isOpen,
  onClose,
  onCreated,
  schemaId: initialSchemaId,
  campaignId,
}: TemplateCreationModalProps) {
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);
  const [isCreatingSchema, setIsCreatingSchema] = useState(false);
  const [schemaCreationError, setSchemaCreationError] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingBackImage, setIsUploadingBackImage] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [creationPhase, setCreationPhase] = useState<"idle" | "saving" | "blockchain" | "complete">("idle");
  const [blockchainStatus, setBlockchainStatus] = useState<{
    success: boolean;
    status: string;
    error?: string;
    transactionId?: string;
  } | null>(null);

  const [formData, setFormData] = useState({
    schemaId: initialSchemaId || "",
    name: "",
    description: "",
    maxSupply: "",
  });

  const [primaryMedia, setPrimaryMedia] = useState<UploadedMedia | null>(null);
  const [backImage, setBackImage] = useState<UploadedMedia | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const backImageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchSchemas();
      // Reset form when modal opens
      setFormData({
        schemaId: initialSchemaId || "",
        name: "",
        description: "",
        maxSupply: "",
      });
      setPrimaryMedia(null);
      setBackImage(null);
      setError("");
      setValidationErrors({});
      setCreationPhase("idle");
      setBlockchainStatus(null);
    }
    // fetchSchemas is defined below but only uses campaignId which doesn't change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSchemaId]);

  const fetchSchemas = async () => {
    try {
      setIsLoadingSchemas(true);
      const params = new URLSearchParams();
      if (campaignId) params.set("campaignId", campaignId);

      const url = `/api/digital-assets/schemas${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch schemas");
      }

      const data = await response.json();
      const schemasList = data.schemas || [];
      setSchemas(schemasList);

      // Auto-select schema if only one exists or initialSchemaId is provided
      if (initialSchemaId) {
        setFormData((prev) => ({ ...prev, schemaId: initialSchemaId }));
      } else if (schemasList.length === 1) {
        setFormData((prev) => ({ ...prev, schemaId: schemasList[0].id }));
      }
    } catch (err) {
      clientLogger.error("Error fetching schemas", {
        error: err instanceof Error ? err.message : "Unknown error"
      });
      setError("Failed to load schemas");
    } finally {
      setIsLoadingSchemas(false);
    }
  };

  /**
   * Inline recovery path for campaigns that don't yet have a schema (e.g.
   * seeded data, campaigns created via the API, or wizard step-2 failures).
   * Fetches the campaign's title so we can use it as the schema name, POSTs
   * to the schemas endpoint, and refetches the list so the new row is
   * auto-selected.
   */
  const handleCreateSchemaForCampaign = async () => {
    if (!campaignId) return;
    setIsCreatingSchema(true);
    setSchemaCreationError(null);
    try {
      const campaignRes = await fetch(`/api/campaigns/${campaignId}`);
      if (!campaignRes.ok) throw new Error("Failed to load campaign details");
      const campaignData = await campaignRes.json();
      const name: string | undefined =
        campaignData?.campaign?.title || campaignData?.title;
      if (!name) throw new Error("Could not determine a schema name from the campaign");

      const schemaRes = await fetch("/api/digital-assets/schemas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, campaignId }),
      });
      const schemaData = await schemaRes.json();
      if (!schemaRes.ok && schemaRes.status !== 409) {
        throw new Error(schemaData?.error || "Failed to create schema");
      }

      await fetchSchemas();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      clientLogger.error("Inline schema creation failed", { error: message });
      setSchemaCreationError(message);
    } finally {
      setIsCreatingSchema(false);
    }
  };

  const isVideoFile = (file: File): boolean => {
    return file.type.startsWith('video/');
  };

  const uploadMedia = async (file: File, setUploading: (v: boolean) => void): Promise<UploadedMedia | null> => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch("/api/digital-assets/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to upload file");
      }

      const data = await response.json();
      return {
        cid: data.cid,
        url: data.url,
        filename: file.name,
        isVideo: isVideoFile(file),
      };
    } catch (err) {
      clientLogger.error("Error uploading file", {
        error: err instanceof Error ? err.message : "Unknown error"
      });
      setError(err instanceof Error ? err.message : "Failed to upload file");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleMediaChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await uploadMedia(file, setIsUploadingImage);
    if (result) {
      setPrimaryMedia(result);
      if (validationErrors.primaryMedia) {
        setValidationErrors((prev) => ({ ...prev, primaryMedia: "" }));
      }
    }
  };

  const handleBackImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await uploadMedia(file, setIsUploadingBackImage);
    if (result) {
      setBackImage(result);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.schemaId) {
      errors.schemaId = "Please select a schema";
    }

    if (!formData.name.trim()) {
      errors.name = "Template name is required";
    }

    if (!primaryMedia) {
      errors.primaryMedia = "Primary image or video is required";
    }

    if (formData.maxSupply && isNaN(parseInt(formData.maxSupply))) {
      errors.maxSupply = "Max supply must be a number";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError("");
    setCreationPhase("saving");
    setBlockchainStatus(null);

    try {
      // Phase 1: Save to database
      setCreationPhase("saving");

      const response = await fetch("/api/digital-assets/templates", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          schemaId: formData.schemaId,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          imgCid: primaryMedia!.isVideo ? undefined : primaryMedia!.cid,
          videoCid: primaryMedia!.isVideo ? primaryMedia!.cid : undefined,
          backimgCid: backImage?.cid || undefined,
          maxSupply: formData.maxSupply ? parseInt(formData.maxSupply) : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create template");
      }

      // Phase 2: Check blockchain result
      setCreationPhase("blockchain");

      if (data.blockchain) {
        setBlockchainStatus({
          success: data.blockchain.success,
          status: data.blockchain.status,
          error: data.blockchain.error,
          transactionId: data.blockchain.transactionId,
        });
      }

      // Phase 3: Complete
      setCreationPhase("complete");

      // Small delay to show success state before closing
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Call onCreated with the new template
      onCreated({
        id: data.template.id,
        onChainTemplateId: data.template.onChainTemplateId,
        name: data.template.name,
        status: data.template.status || "PENDING",
        imageUrl: data.template.imageUrl,
        videoUrl: data.template.videoUrl,
      });
    } catch (err) {
      clientLogger.error("Error creating template", {
        error: err instanceof Error ? err.message : "Unknown error"
      });
      setError(err instanceof Error ? err.message : "An error occurred");
      setCreationPhase("idle");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Create New Template</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Schema Selection */}
          <div>
            <Label htmlFor="modalSchemaId">Schema *</Label>
            {isLoadingSchemas ? (
              <div className="mt-1 h-10 bg-gray-100 rounded-md animate-pulse"></div>
            ) : schemas.length === 0 ? (
              <div className="mt-1 p-3 bg-yellow-50 border border-yellow-200 rounded-md space-y-2">
                {campaignId ? (
                  <>
                    <p className="text-sm text-yellow-800">
                      This campaign doesn&apos;t have a schema yet. A schema is
                      required before you can mint NFT templates.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateSchemaForCampaign}
                      disabled={isCreatingSchema}
                    >
                      {isCreatingSchema ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating schema…
                        </>
                      ) : (
                        "Create schema for this campaign"
                      )}
                    </Button>
                    {schemaCreationError && (
                      <p className="text-sm text-red-600">{schemaCreationError}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-yellow-800">
                    No schemas found. Please create a schema first.
                  </p>
                )}
              </div>
            ) : (
              <Select
                id="modalSchemaId"
                value={formData.schemaId}
                onChange={(e) => handleChange("schemaId", e.target.value)}
                className={`mt-1 ${validationErrors.schemaId ? "border-red-500" : ""}`}
              >
                <SelectItem value="">Select a schema</SelectItem>
                {schemas.map((schema) => (
                  <SelectItem key={schema.id} value={schema.id}>
                    {schema.schemaName}
                  </SelectItem>
                ))}
              </Select>
            )}
            {validationErrors.schemaId && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.schemaId}</p>
            )}
          </div>

          {/* Template Name */}
          <div>
            <Label htmlFor="modalTemplateName">Template Name *</Label>
            <Input
              id="modalTemplateName"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g., Location 1 Badge"
              className={`mt-1 ${validationErrors.name ? "border-red-500" : ""}`}
            />
            {validationErrors.name && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
            )}
          </div>

          {/* Primary Image or Video */}
          <div>
            <Label htmlFor="modalPrimaryMedia">Primary Image or Video *</Label>
            <div className="mt-1">
              {primaryMedia ? (
                <div className="relative inline-block">
                  {primaryMedia.isVideo ? (
                    <video
                      src={primaryMedia.url}
                      className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                      muted
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <Image
                      src={primaryMedia.url}
                      alt="Primary"
                      width={96}
                      height={96}
                      className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                      unoptimized
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => setPrimaryMedia(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => imageInputRef.current?.click()}
                  className={`w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors ${
                    validationErrors.primaryMedia ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  {isUploadingImage ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400" />
                      <span className="mt-1 text-xs text-gray-500">Upload</span>
                    </>
                  )}
                </div>
              )}
              <input
                ref={imageInputRef}
                type="file"
                id="modalPrimaryMedia"
                accept="image/*,video/*"
                onChange={handleMediaChange}
                className="hidden"
              />
              {validationErrors.primaryMedia && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.primaryMedia}</p>
              )}
            </div>
          </div>

          {/* Back Image (Optional) */}
          <div>
            <Label htmlFor="modalBackImage">Back Image (Optional)</Label>
            <div className="mt-1">
              {backImage ? (
                <div className="relative inline-block">
                  <Image
                    src={backImage.url}
                    alt="Back"
                    width={96}
                    height={96}
                    className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                    unoptimized
                  />
                  <button
                    type="button"
                    onClick={() => setBackImage(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => backImageInputRef.current?.click()}
                  className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  {isUploadingBackImage ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  ) : (
                    <>
                      <ImageIcon className="w-6 h-6 text-gray-400" />
                      <span className="mt-1 text-xs text-gray-500">Back</span>
                    </>
                  )}
                </div>
              )}
              <input
                ref={backImageInputRef}
                type="file"
                id="modalBackImage"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleBackImageChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Description (Optional) */}
          <div>
            <Label htmlFor="modalDescription">Description (Optional)</Label>
            <textarea
              id="modalDescription"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Describe this NFT..."
              rows={2}
              className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* Max Supply (Optional) */}
          <div>
            <Label htmlFor="modalMaxSupply">Max Supply (Optional)</Label>
            <Input
              id="modalMaxSupply"
              type="number"
              value={formData.maxSupply}
              onChange={(e) => handleChange("maxSupply", e.target.value)}
              placeholder="Leave blank for unlimited"
              min="1"
              className={`mt-1 ${validationErrors.maxSupply ? "border-red-500" : ""}`}
            />
            {validationErrors.maxSupply && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.maxSupply}</p>
            )}
          </div>

          {/* Creation Progress */}
          {isLoading && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="space-y-3">
                {/* Saving Phase */}
                <div className="flex items-center gap-2">
                  {creationPhase === "saving" ? (
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  ) : creationPhase !== "idle" ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                  )}
                  <span className={creationPhase === "saving" ? "text-blue-700 font-medium" : creationPhase !== "idle" ? "text-green-700" : "text-gray-500"}>
                    Saving template...
                  </span>
                </div>

                {/* Blockchain Phase */}
                <div className="flex items-center gap-2">
                  {creationPhase === "blockchain" ? (
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  ) : creationPhase === "complete" ? (
                    blockchainStatus?.success ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                    )
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                  )}
                  <span className={
                    creationPhase === "blockchain" ? "text-blue-700 font-medium" :
                    creationPhase === "complete" && blockchainStatus?.success ? "text-green-700" :
                    creationPhase === "complete" && !blockchainStatus?.success ? "text-yellow-700" :
                    "text-gray-500"
                  }>
                    {creationPhase === "blockchain" ? "Creating on blockchain..." :
                     creationPhase === "complete" && blockchainStatus?.success ? "Created on blockchain!" :
                     creationPhase === "complete" && !blockchainStatus?.success ? "Saved as draft (blockchain pending)" :
                     "Creating on blockchain..."}
                  </span>
                </div>

                {/* Blockchain Status Details */}
                {creationPhase === "complete" && blockchainStatus && (
                  <div className={`mt-2 p-2 rounded text-sm ${blockchainStatus.success ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                    {blockchainStatus.success ? (
                      <>
                        <p className="font-medium">Template created successfully!</p>
                        {blockchainStatus.transactionId && (
                          <p className="text-xs mt-1 font-mono">TX: {blockchainStatus.transactionId.substring(0, 16)}...</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="font-medium">Template saved as draft</p>
                        <p className="text-xs mt-1">{blockchainStatus.error || "Will be created on blockchain when campaign is published."}</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || isUploadingImage || isUploadingBackImage || schemas.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {creationPhase === "saving" ? "Saving..." :
                   creationPhase === "blockchain" ? "Creating on blockchain..." :
                   creationPhase === "complete" ? "Done!" : "Creating..."}
                </>
              ) : (
                "Create Template"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
